import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import BillingDetailSheet from '../../components/BillingDetailSheet';
import { fmtLocalDate } from '../../lib/timeFmt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  paid: 'text-green-500 bg-green-500/10',
  deposit_paid: 'text-blue-400 bg-blue-500/10',
  accepted: 'text-emerald-400 bg-emerald-500/10',
  pending: 'text-[#D4AF37] bg-[#D4AF37]/10',
  declined: 'text-red-500 bg-red-500/10',
  overdue: 'text-red-500 bg-red-500/10',
};

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    // First, reconcile any pending Stripe payments (e.g., user closed tab during polling)
    try {
      const { data } = await axios.post(`${API}/invoices/sync-pending`, {}, { withCredentials: true });
      if (data.synced > 0) toast.success(`${data.synced} payment(s) confirmed`);
    } catch { /* ignore */ }
    await fetchInvoices();
    checkPaymentStatus();
  };

  const fetchInvoices = async () => {
    try {
      const { data } = await axios.get(`${API}/invoices`, { withCredentials: true });
      setInvoices(data);
    } catch (error) { console.error('Error fetching invoices:', error); }
  };

  const checkPaymentStatus = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('invoice_session_id');
    if (!sessionId) return;
    setVerifyingPayment(true);
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 30) {
        toast.error('Payment verification timed out. Pull down to refresh — your payment may still complete shortly.');
        setVerifyingPayment(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/invoices/checkout/status/${sessionId}`, { withCredentials: true });
        if (data.payment_status === 'paid') {
          toast.success('Payment successful — thank you!');
          window.history.replaceState({}, document.title, window.location.pathname);
          await fetchInvoices();
          setVerifyingPayment(false);
          return;
        }
        if (data.status === 'expired') {
          toast.error('Payment session expired.');
          setVerifyingPayment(false);
          return;
        }
        attempts++;
        setTimeout(poll, 2000);
      } catch (err) {
        attempts++;
        setTimeout(poll, 2000);
      }
    };
    poll();
  };

  return (
    <div data-testid="invoices-page" className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Invoices</h2>
          <p className="text-[#A3A3A3]">View, accept, and pay your invoices</p>
        </div>
        <button
          data-testid="refresh-invoices-btn"
          onClick={init}
          className="text-[#D4AF37] hover:text-[#F3E5AB] text-sm border border-[#262626] hover:border-[#D4AF37] rounded-md px-4 py-2 transition-colors flex items-center gap-2"
        >
          <Loader2 className={`w-4 h-4 ${verifyingPayment ? 'animate-spin' : ''}`} />
          Refresh & Sync Payments
        </button>
      </div>

      {verifyingPayment && (
        <div className="bg-[#141414] border border-[#D4AF37] rounded-md p-4 flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
          <p className="text-white text-sm">Verifying your payment...</p>
        </div>
      )}

      {/* Our Promise to You */}
      <div data-testid="our-promise-card" className="bg-[#141414] border border-[#262626] rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] uppercase mb-2">Our Promise to You</p>
          <h3 className="font-['Outfit'] text-xl font-bold text-white mb-3">Premium Plumbing. Premium Care.</h3>
          <ul className="space-y-2">
            <li className="flex gap-2 text-white text-sm"><span className="text-[#D4AF37]">✓</span> We show up on time.</li>
            <li className="flex gap-2 text-white text-sm"><span className="text-[#D4AF37]">✓</span> We do the job right.</li>
            <li className="flex gap-2 text-white text-sm"><span className="text-[#D4AF37]">✓</span> We treat your home like it's our own.</li>
          </ul>
        </div>
        <div className="text-center md:text-right">
          <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Need Help? Call or Text</p>
          <a href="tel:6142854502" className="text-[#D4AF37] text-3xl font-bold hover:text-[#F3E5AB]">614-285-4502</a>
          <p className="text-[#A3A3A3] text-sm mt-2">24/7 Emergency Plumbing Services</p>
        </div>
      </div>

      <div className="grid gap-4">
        {invoices.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">No invoices yet</div>
        ) : invoices.map((invoice) => {
          const amountPaid = invoice.amount_paid || 0;
          const balance = Math.max(0, invoice.amount - amountPaid);
          const needsAction = invoice.status === 'pending' || (['accepted', 'deposit_paid'].includes(invoice.status) && balance > 0);
          // iter156 — Membership Included / $0 service record (covered under active NexFlow membership)
          const isMembershipService = !!invoice.covered_by_membership || invoice.invoice_kind === 'membership_service';
          return (
            <button
              key={invoice.id}
              data-testid={`customer-invoice-${invoice.id}`}
              onClick={() => setSelected(invoice)}
              className={`w-full text-left bg-[#141414] border ${isMembershipService ? 'border-blue-500/40' : (needsAction ? 'border-[#D4AF37]' : 'border-[#262626]')} rounded-md p-6 hover:border-[#D4AF37] transition-all duration-200`}
            >
              <div className="flex items-start justify-between">
                <div className="flex gap-4 flex-1">
                  <div className={`w-12 h-12 ${isMembershipService ? 'bg-blue-500/10' : 'bg-[#D4AF37]/10'} rounded-md flex items-center justify-center`}>
                    <FileText className={`w-6 h-6 ${isMembershipService ? 'text-blue-400' : 'text-[#D4AF37]'}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-medium text-lg mb-1 flex items-center gap-2 flex-wrap">
                      {invoice.invoice_number}
                      {invoice.from_estimate_number && <span className="text-xs text-[#A3A3A3] italic">from {invoice.from_estimate_number}</span>}
                      {isMembershipService && (
                        <span data-testid={`membership-badge-${invoice.id}`} className="text-[10px] font-bold tracking-wider uppercase text-blue-300 bg-blue-500/15 border border-blue-500/30 rounded px-2 py-0.5">
                          Membership Service
                        </span>
                      )}
                    </h3>
                    <p className="text-[#A3A3A3] text-sm mb-2 line-clamp-1">{invoice.notes || invoice.description || invoice.line_items?.[0]?.description || ''}</p>
                    <p className="text-[#A3A3A3] text-xs">{fmtLocalDate(invoice.service_date || invoice.date)}</p>
                    {isMembershipService ? (
                      <p className="text-blue-300 text-xs font-bold mt-2 uppercase tracking-wider">
                        $0 Due — Covered under active NexFlow membership
                      </p>
                    ) : needsAction && (
                      <p className="text-[#D4AF37] text-xs font-bold mt-2 uppercase tracking-wider">
                        {invoice.status === 'pending' ? '→ Action Required: Accept or Decline' : '→ Payment Due'}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold mb-1 ${isMembershipService ? 'text-blue-300' : 'text-[#D4AF37]'}`}>${invoice.amount.toFixed(2)}</p>
                  {amountPaid > 0 && <p className="text-green-400 text-xs mb-1">Paid: ${amountPaid.toFixed(2)}</p>}
                  {invoice.deposit_amount > 0 && invoice.status === 'pending' && <p className="text-[#A3A3A3] text-xs mb-1">Deposit: ${invoice.deposit_amount.toFixed(2)}</p>}
                  <div className={`inline-block px-3 py-1 rounded-md text-xs font-medium ${isMembershipService ? 'text-blue-300 bg-blue-500/10' : (STATUS_COLORS[invoice.status] || 'text-[#A3A3A3] bg-[#262626]')}`}>
                    {isMembershipService ? 'MEMBERSHIP — $0 DUE' : (invoice.status || 'pending').replace('_', ' ').toUpperCase()}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <BillingDetailSheet
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        doc={selected}
        type="invoice"
        viewerRole="customer"
        onChange={fetchInvoices}
      />
    </div>
  );
};

export default Invoices;
